import type { DocPageContent } from '../docs-content'

// order 11.5 places this right after Text, Captions, and Subtitles in Creative Tools.
const page = {
  order: 11.5,
  slug: 'shapes-masks',
  title: '形状与遮罩',
  description:
    '添加形状项、用钢笔工具绘制自定义路径，并将任意形状用作片段遮罩。',
  category: '创意工具',
  related: ['motion', 'properties', 'effects-color', 'keyframes'],
  sections: [
    {
      title: '添加形状',
      blocks: [
        {
          kind: 'list',
          items: [
            '打开**形状**标签页并点击一个形状，将其添加到播放头处，或将其拖到特定轨道上。',
            '内置形状：**矩形**、**圆形**、**椭圆**、**三角形**、**星形**、**多边形**与**心形**。',
            '新形状会以适配画布的尺寸落下并被选中，随时可以定位与设置样式。',
          ],
        },
      ],
    },
    {
      title: '设置形状样式',
      blocks: [
        {
          kind: 'table',
          headers: ['控件', '说明'],
          rows: [
            ['类型', '在七种形状类型之间切换。'],
            ['填充', '纯色或渐变填充。'],
            ['描边宽度 / 描边', '轮廓宽度（0–50 px）及其颜色（宽度大于 0 时显示）。'],
            ['端点 / 连接', '描边端点，以及相连描边段的衔接方式。'],
            ['修剪路径', '用于只绘制路径一部分的起始、结束与偏移控件。'],
            ['锥形', '锥形描边的起点与终点宽度。'],
            ['圆角半径', '矩形、三角形、星形与多边形的圆角（0–100 px）。'],
            ['点数 / 内半径', '星形与多边形的点数与内半径。'],
            ['方向', '三角形所指向的方向。'],
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '位置、大小、旋转与不透明度位于变换控件中，且大多数数值都可以打关键帧——参见 [关键帧动画](keyframes)。',
        },
      ],
    },
    {
      title: '在运动中为路径顶点制作动画',
      blocks: [
        {
          kind: 'steps',
          items: [
            '在**运动**中打开形状并展开其属性行。',
            '在预览中选择路径点。',
            '选择**所选顶点**可仅为这些点打关键帧，或选择**所有顶点**以暴露每一个点。',
            '为顶点坐标添加关键帧，并在重塑路径前移动播放头。',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '路径几何（Path Geometry）的关键帧会锁定路径拓扑。在添加、移除、重新排序或更改第一个顶点之前，请先移除它们。',
        },
      ],
    },
    {
      title: '用钢笔工具绘制自定义路径',
      blocks: [
        {
          kind: 'steps',
          items: [
            '在形状标签页中，选择**钢笔**工具以绘制自定义路径形状。',
            '在预览中点击以放置点；点击并拖动某点可拉出曲线（贝塞尔）手柄。',
            '按退格键删除最后一个点，按 Esc 取消。',
            '在至少三个点后，选择**完成形状**以提交它（或**取消**）。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '若要稍后重塑路径，请选中它并使用**编辑路径**：拖动点与手柄，双击边缘以添加点，并在**角点**与**贝塞尔**之间转换点的类型。完成后选择**完成**。',
        },
      ],
    },
    {
      title: '将形状用作遮罩',
      blocks: [
        {
          kind: 'steps',
          items: [
            '将形状放置在你想遮罩的片段**上方**的轨道上。',
            '在形状部分，开启**用作遮罩**。',
            '选择**遮罩类型**：**裁剪**为硬边，**Alpha** 为软边。',
            '对于 Alpha 遮罩，调整**羽化**以柔化边缘；使用**反相**来隐藏形状区域而非显现它。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '遮罩会按其形状几何，影响下方轨道上的一切。羽化仅适用于 Alpha 遮罩——裁剪遮罩始终保持硬边。',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '当你编辑遮罩或用钢笔绘制时，编辑器的其余部分会被锁定——请完成或退出遮罩编辑以继续。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
